import React, { useState, useEffect, useRef } from 'react';
import { Send, Building2, User, Phone, CheckCircle, XCircle } from 'lucide-react';
import ChatBubble from './ChatBubble';
import { chatApi } from '../../api/chatApi';
import { employeeApi } from '../../api/employeeApi';
import toast from 'react-hot-toast';

const ChatWindow = ({ selectedAdmin, currentUserId, socket }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const fetchHistory = async () => {
      if (!selectedAdmin) return;
      setLoading(true);
      try {
        const res = await chatApi.getChatHistory(selectedAdmin._id);
        setMessages(res.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [selectedAdmin]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Socket listeners
  useEffect(() => {
    if (!socket || !selectedAdmin) return;

    const handleReceiveMessage = (newMsg) => {
      if (
        newMsg.senderId === selectedAdmin._id ||
        newMsg.receiverId === selectedAdmin._id
      ) {
        setMessages((prev) => {
          if (prev.some((m) => m._id === newMsg._id || (m.clientMessageId && m.clientMessageId === newMsg.clientMessageId))) {
            return prev;
          }
          return [...prev, newMsg];
        });
      }
    };

    const handleUserTyping = ({ senderId, isTyping: typingState }) => {
      if (senderId === selectedAdmin._id) {
        setIsTyping(typingState);
      }
    };

    const handleLimitUpdated = ({ messageId, status, note }) => {
      setMessages((prev) =>
        prev.map((msg) => {
          if (msg._id === messageId && msg.limitDetails) {
            return {
              ...msg,
              limitDetails: { ...msg.limitDetails, status, note }
            };
          }
          return msg;
        })
      );
    };

    socket.on('receive_message', handleReceiveMessage);
    socket.on('new_chat_message', handleReceiveMessage);
    socket.on('user_typing', handleUserTyping);
    socket.on('limit_request_updated', handleLimitUpdated);

    return () => {
      socket.off('receive_message', handleReceiveMessage);
      socket.off('new_chat_message', handleReceiveMessage);
      socket.off('user_typing', handleUserTyping);
      socket.off('limit_request_updated', handleLimitUpdated);
    };
  }, [socket, selectedAdmin]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || !selectedAdmin) return;

    const msgData = {
      senderId: currentUserId,
      senderModel: 'SuperAdmin',
      receiverId: selectedAdmin._id,
      companyId: selectedAdmin.company?._id,
      message: inputMessage.trim()
    };

    if (socket && socket.connected) {
      socket.emit('send_message', msgData);
    } else {
      chatApi.sendMessage(msgData);
    }

    // Optimistic UI push
    setMessages((prev) => [
      ...prev,
      {
        _id: Date.now().toString(),
        ...msgData,
        createdAt: new Date().toISOString()
      }
    ]);

    setInputMessage('');
  };

  const handleInputChange = (e) => {
    setInputMessage(e.target.value);
    if (socket && selectedAdmin) {
      socket.emit('typing', {
        senderId: currentUserId,
        receiverId: selectedAdmin._id,
        isTyping: e.target.value.length > 0
      });
    }
  };

  const handleApproveLimit = async (messageId, requestedLimit) => {
    try {
      await employeeApi.approveLimitRequest(messageId, { newLimit: requestedLimit });
      toast.success(`Approved limit increase to ${requestedLimit}!`);
      setMessages((prev) =>
        prev.map((m) =>
          m._id === messageId
            ? { ...m, limitDetails: { ...m.limitDetails, status: 'Approved' } }
            : m
        )
      );
    } catch (err) {
      toast.error(err.message || 'Failed to approve limit');
    }
  };

  const handleRejectLimit = async (messageId) => {
    try {
      await employeeApi.rejectLimitRequest(messageId, { note: 'Rejected via Chat' });
      toast.error('Limit request rejected');
      setMessages((prev) =>
        prev.map((m) =>
          m._id === messageId
            ? { ...m, limitDetails: { ...m.limitDetails, status: 'Rejected' } }
            : m
        )
      );
    } catch (err) {
      toast.error(err.message || 'Failed to reject limit');
    }
  };

  if (!selectedAdmin) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 bg-white text-slate-500 text-center">
        <User className="w-12 h-12 mb-3 text-slate-700" />
        <p className="text-sm font-semibold text-slate-600">Select a Company Admin</p>
        <p className="text-xs text-slate-500 max-w-xs mt-1">
          Pick an admin from the sidebar list to start real-time messaging and process limit requests.
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full bg-white">
      {/* Chat Window Header */}
      <div className="px-6 py-4 border-b border-slate-100 bg-white/90 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <img
              src={selectedAdmin.avatar || 'https://images.unsplash.com/photo-1570295999919-56ceb5ecca61?w=150&auto=format&fit=crop&q=80'}
              alt={selectedAdmin.name}
              className="w-10 h-10 rounded-full object-cover border border-slate-700"
            />
            <span
              className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-slate-900 ${
                selectedAdmin.isOnline ? 'bg-emerald-500' : 'bg-slate-600'
              }`}
            />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-900">{selectedAdmin.name}</h3>
            <p className="text-xs text-green-700 flex items-center space-x-1">
              <Building2 className="w-3 h-3" />
              <span>{selectedAdmin.company?.name || 'Company Admin'}</span>
            </p>
          </div>
        </div>

        <div className="text-right">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
            {selectedAdmin.isOnline ? 'Active Now' : 'Offline'}
          </span>
        </div>
      </div>

      {/* Messages Body */}
      <div className="flex-1 overflow-y-auto p-6 space-y-2">
        {loading ? (
          <div className="text-center py-8 text-xs text-slate-500">Loading conversation...</div>
        ) : messages.length === 0 ? (
          <div className="text-center py-12 text-xs text-slate-500">
            No previous messages with {selectedAdmin.name}. Say hello!
          </div>
        ) : (
          messages.map((msg) => (
            <ChatBubble
              key={msg._id}
              message={msg}
              currentUserId={currentUserId}
              onApproveLimit={handleApproveLimit}
              onRejectLimit={handleRejectLimit}
            />
          ))
        )}

        {isTyping && (
          <div className="text-xs text-green-700 italic animate-pulse py-1">
            {selectedAdmin.name} is typing...
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input Footer */}
      <form onSubmit={handleSendMessage} className="p-4 border-t border-slate-100 bg-white">
        <div className="flex items-center space-x-3">
          <input
            type="text"
            value={inputMessage}
            onChange={handleInputChange}
            placeholder={`Message ${selectedAdmin.name}...`}
            className="flex-1 px-4 py-2.5 rounded-xl bg-white border border-slate-100 text-xs sm:text-sm text-slate-700 placeholder-slate-500 focus:outline-none focus:border-green-600 transition"
          />
          <button
            type="submit"
            disabled={!inputMessage.trim()}
            className="p-2.5 rounded-xl bg-green-700 hover:bg-green-600 text-white shadow-lg shadow-green-700/30 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChatWindow;
